import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { CheckCircleOutlined, ReportProblemOutlined } from '../../icons';
import DashboardTopbar from '../../components/DashboardTopbar';
import Dropdown from '../../components/Dropdown';
import QuestionNavigator from '../../components/QuestionNavigator/QuestionNavigator';
import QuestionBlock, { QuestionOption } from '../../components/QuestionBlock/QuestionBlock';
import { isValidOptionText } from '../../utils/questionOptions';
import { useCourse } from '../../contexts/CourseContext';
import '../FreeQuestions/FreeQuestions.css';
import './WrongAnswers.css';

const apiUrl = process.env.REACT_APP_API_URL;

const AWNSERS = ['A', 'B', 'C', 'D', 'E'];

function WrongAnswers() {
  const navigate = useNavigate();
  const { activeCourseId } = useCourse();
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingSubjects, setLoadingSubjects] = useState(true);
  const [loadingQuestions, setLoadingQuestions] = useState(true);

  const getQuestionImageSrc = (imageUrl) => {
    if (!imageUrl) return null;
    if (imageUrl.startsWith('data:')) return imageUrl;
    if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://') || imageUrl.startsWith('//')) return imageUrl;
    return `${apiUrl}uploads/${imageUrl}`;
  };

  const fetchWrongAnswers = useCallback(async (subjectId, courseId) => {
    setLoadingQuestions(true);
    try {
      const params = { ...(subjectId ? { subjectId } : {}), ...(courseId ? { courseId } : {}) };
      const response = await axios.get(apiUrl + 'wrongAnswers', { params });
      setQuestions(response.data.questions || []);
      setCurrentIndex(0);
    } catch {
      alert('Erro ao carregar suas questões erradas. Tente novamente mais tarde.');
    } finally {
      setLoadingQuestions(false);
    }
  }, []);

  useEffect(() => {
    const fetchSubjects = async () => {
      setLoadingSubjects(true);
      try {
        const params = activeCourseId ? { scope: 'free', courseId: activeCourseId } : {};
        const response = await axios.get(apiUrl + 'subjects', { params });
        setSubjects(response.data.subjects || []);
      } catch {
        alert('Erro ao carregar as matérias. Tente novamente mais tarde.');
      } finally {
        setLoadingSubjects(false);
      }
    };

    fetchSubjects();
    fetchWrongAnswers(selectedSubjectId || null, activeCourseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchWrongAnswers, activeCourseId]);

  const handleSubjectChange = (subjectId) => {
    setSelectedSubjectId(subjectId);
    fetchWrongAnswers(subjectId || null, activeCourseId);
  };

  const selectedSubjectName = subjects.find(
    (subject) => String(subject.id) === String(selectedSubjectId)
  )?.name;

  const totalQuestions = questions.length;
  const currentQuestion = questions[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === totalQuestions - 1;
  const goPrev = () => setCurrentIndex((i) => Math.max(0, i - 1));
  const goNext = () => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1));

  if (loadingSubjects) {
    return (
      <div className="free-questions-page">
        <DashboardTopbar label="Meus erros" />
        <div className="loading-message">Carregando matérias...</div>
      </div>
    );
  }

  return (
    <div className="free-questions-page free-questions-page--wide">
      <DashboardTopbar label="Meus erros" />

      <h2 className="free-questions-title">Meus erros</h2>
      <p className="free-questions-subtitle">
        Revise as questões que você errou em simulados e questões livres, com a explicação de cada uma.
      </p>

      <div className="filtro-container">
        <span className="filtro-label">
          Filtrar por matéria:
        </span>
        <Dropdown
          value={selectedSubjectId}
          onChange={handleSubjectChange}
          options={[
            { value: '', label: 'Todas as matérias' },
            ...subjects.map((subject) => ({ value: String(subject.id), label: subject.name })),
          ]}
          headerLabel="Selecione uma matéria"
          placeholder="Todas as matérias"
          ariaLabel="Filtrar por matéria"
          className="filtro-dropdown dropdown--stretch"
        />

        {selectedSubjectName && (
          <div className="filtro-selecionado">
            <p>Mostrando erros de: <strong>{selectedSubjectName}</strong></p>
          </div>
        )}
      </div>

      {loadingQuestions ? (
        <div className="loading-message">Carregando suas questões erradas...</div>
      ) : totalQuestions === 0 ? (
        <div className="empty-message wrong-answers-empty">
          <CheckCircleOutlined style={{ fontSize: 32, color: 'var(--success-color)', display: 'block', margin: '0 auto 8px' }} />
          Nenhum erro por aqui — continue assim!
        </div>
      ) : (
        <div className="single-question-layout">
          <QuestionNavigator
            title="Navegador"
            count={totalQuestions}
            currentIndex={currentIndex}
            onSelect={setCurrentIndex}
            getStatus={() => ''}
          />

          <div className="single-question-main">
            <div className="wrong-answer-meta">
              {currentQuestion.subjectName && (
                <span className="subject-badge">{currentQuestion.subjectName}</span>
              )}
              <span className="mock-origin-badge">{currentQuestion.mockName}</span>
            </div>
            <QuestionBlock
              counterText={`Questão ${currentIndex + 1} de ${totalQuestions}`}
              title={currentQuestion.title}
              imageSrc={currentQuestion.image_url ? getQuestionImageSrc(currentQuestion.image_url) : null}
              imageAlt={`Imagem da questão ${currentQuestion.questionId}`}
            >
              {currentQuestion.choices.map((choice, choiceIndex) => {
                if (!isValidOptionText(choice)) {
                  return null;
                }
                const letter = AWNSERS[choiceIndex];
                const isCorrect = letter === currentQuestion.correct;
                const isChosenWrong = choiceIndex === currentQuestion.choiceSelected && !isCorrect;
                return (
                  <QuestionOption
                    key={choiceIndex}
                    index={choiceIndex}
                    letter={`${letter})`}
                    text={choice}
                    status={isCorrect ? 'correct' : (isChosenWrong ? 'wrong' : '')}
                    icon={isCorrect ? (
                      <CheckCircleOutlined style={{ fontSize: 16, verticalAlign: 'middle' }} />
                    ) : isChosenWrong ? (
                      <ReportProblemOutlined style={{ fontSize: 16, verticalAlign: 'middle', color: 'var(--error-color)' }} />
                    ) : null}
                  />
                );
              })}
            </QuestionBlock>
            <div className="wrong-answer-explanation">
              <b>Explicação: </b>{currentQuestion.explication}
            </div>

            <div className="single-question-nav-buttons">
              <button type="button" onClick={goPrev} disabled={isFirst}>← Anterior</button>
              <button type="button" onClick={goNext} disabled={isLast}>Próxima →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WrongAnswers;
